import { Link } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { ArrowRight, MousePointerClick, RefreshCw, Tag } from "lucide-react";
import { LogoMark } from "@/components/brand/Logo";
import { Button } from "@/components/ui/button";
import { SectionHeading } from "./SectionHeading";
import { trackEvent } from "@/lib/umami";

const FEATURES = [
  { key: "fill", icon: MousePointerClick },
  { key: "label", icon: Tag },
  { key: "reuse", icon: RefreshCw },
] as const;

/** Fictional site and the site-style address the extension would create for it. */
const MOCK_SITE = "trailgear.shop";
const MOCK_ADDRESS = "trailgear.k3x9@mail.northfield.dev";

/**
 * Browser extension teaser: the in-form button and panel, drawn as an
 * illustration (fixed light palette, like the hero mock), next to the three
 * things it does. Links to /extension for the install steps.
 */
export function ExtensionSection() {
  const { t } = useTranslation("landing");

  return (
    <section
      id="extension"
      className="mx-auto max-w-[1200px] scroll-mt-24 px-5 pt-24 md:px-10 md:pt-32"
    >
      <SectionHeading
        eyebrow={t("extensionPromo.eyebrow")}
        title={t("extensionPromo.title")}
        subtitle={t("extensionPromo.subtitle")}
      />

      <div className="mt-10 grid gap-10 lg:grid-cols-[1fr_1fr] lg:items-center lg:gap-16">
        {/* Illustration of a signup form with the extension panel open.
            min-w-0 is load-bearing: the mock's mono address is nowrap, so without
            it this grid item keeps its content width and scrolls the page
            sideways at 375px. */}
        <div
          aria-hidden="true"
          className="relative min-w-0 rounded-2xl p-5 pb-40 bg-mesh-low sm:p-8 sm:pb-36 md:bg-mesh"
        >
          <div className="rounded-xl bg-white p-5 text-[#0a2540] shadow-float sm:p-6">
            <div className="flex items-center justify-between">
              <span className="font-semibold">{t("extensionPromo.mock.formTitle")}</span>
              <span className="font-mono text-xs text-[#6b7c93]">{MOCK_SITE}</span>
            </div>
            <label className="mt-5 block text-xs font-medium text-[#425466]">
              {t("extensionPromo.mock.emailLabel")}
            </label>
            <div className="mt-1.5 flex h-10 items-center gap-2 rounded-lg border-2 border-[#2e6bff] px-3">
              <span className="min-w-0 flex-1 truncate font-mono text-[13px]">{MOCK_ADDRESS}</span>
              <LogoMark size={20} />
            </div>
            <div className="mt-3 h-10 rounded-lg border border-[#e3e8ee] bg-[#f6f9fc]" />
          </div>

          <div className="absolute right-5 bottom-5 left-5 rounded-[14px] border border-[#e3e8ee] bg-white p-3 text-[13px] text-[#0a2540] shadow-float sm:right-8 sm:left-auto sm:w-[300px]">
            <div className="mb-2 flex items-center gap-2 font-semibold">
              <LogoMark size={18} />
              Mailfinity
            </div>
            <p className="font-semibold text-[#00806a]">{t("extensionPromo.mock.filled")}</p>
            <p className="mt-1 text-[#425466]">
              {t("extensionPromo.mock.labelledAs")}{" "}
              <b className="font-semibold text-[#0a2540]">{MOCK_SITE}</b>
            </p>
            <div className="mt-2.5 truncate rounded-lg bg-[#eef2f7] px-2.5 py-1.5 font-mono text-xs">
              {MOCK_ADDRESS}
            </div>
          </div>
        </div>

        <div>
          <ul className="grid gap-6">
            {FEATURES.map(({ key, icon: Icon }) => (
              <li key={key} className="flex gap-4">
                <span className="flex size-9 shrink-0 items-center justify-center rounded-xl bg-accent text-primary">
                  <Icon className="size-4" />
                </span>
                <div>
                  <h3 className="font-semibold">{t(`extension.features.${key}.title`)}</h3>
                  <p className="mt-1 text-[15px] leading-relaxed text-muted-foreground">
                    {t(`extension.features.${key}.body`)}
                  </p>
                </div>
              </li>
            ))}
          </ul>
          <div className="mt-8 flex flex-wrap items-center gap-x-4 gap-y-3">
            <Button asChild size="lg">
              <Link
                to="/extension"
                onClick={() => trackEvent("extension_cta_click", { location: "landing" })}
              >
                {t("extensionPromo.cta")}
                <ArrowRight />
              </Link>
            </Button>
            <span className="text-sm text-muted-foreground">{t("extension.browsers")}</span>
          </div>
        </div>
      </div>
    </section>
  );
}
